import React from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../../components/blocks/AnimatedSection';
import FacultyAdvisorCard from '../../components/blocks/FacultyAdvisorCard';
import { getAdvisorsByKeys } from '../../config/advisors';
import { getCortexEventBySlug } from '../../config/cortexEvents';
import {
  CORTEX_KEYNOTE_SPEECH_2026_SPEAKER_KEY,
  CORTEX_KEYNOTE_SPEECH_2026_SESSION,
  CORTEX_KEYNOTE_SPEECH_2026_VENUE,
  CORTEX_KEYNOTE_SPEECH_2026_INSTAGRAM,
  CORTEX_KEYNOTE_SPEECH_2026_ABSTRACT,
  CORTEX_KEYNOTE_SPEECH_2026_KEYNOTE_ABSTRACT_TITLE,
  CORTEX_KEYNOTE_SPEECH_2026_KEYNOTE_ABSTRACT_PARAGRAPHS,
  CORTEX_KEYNOTE_SPEECH_2026_BIBLIOGRAPHY,
} from '../../config/cortexKeynoteSpeech2026';
import { linkifyTextToReact } from '../../utils/linkifyText';
import { cortexOutlineButtonClass } from '../../config/themeColors';

const KeynoteSpeech2026 = () => {
  const speaker = getAdvisorsByKeys([CORTEX_KEYNOTE_SPEECH_2026_SPEAKER_KEY], 'blue')[0];
  const keynoteEvent = getCortexEventBySlug('keynote-speech-2026');

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-sm font-semibold uppercase tracking-widest text-blue-800 mb-2">CORTEX</p>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Keynote Speech</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">{CORTEX_KEYNOTE_SPEECH_2026_SESSION}</p>
          <div className="mt-6 flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3">
            <a
              href={keynoteEvent?.ctaHref || CORTEX_KEYNOTE_SPEECH_2026_INSTAGRAM}
              target="_blank"
              rel="noopener noreferrer"
              className={cortexOutlineButtonClass}
            >
              {keynoteEvent?.ctaLabel || 'View on Instagram'}
            </a>
            <Link to="/cortex/conference-2026" className={cortexOutlineButtonClass}>
              Conference 2026
            </Link>
          </div>
        </div>

        {speaker && (
          <div className="mb-8 flex justify-center">
            <div className="max-w-xs w-full">
              <FacultyAdvisorCard {...speaker} link={undefined} rimHighlight footerBadge="CORTEX 2026 keynote speaker" />
            </div>
          </div>
        )}

        <AnimatedSection
          className="mb-12"
          animateOnScroll={false}
          heading={<h2 className="text-2xl font-semibold text-gray-900 mb-6">Session Details</h2>}
        >
          <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 grid md:grid-cols-2 gap-8 items-start">
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-1">Date &amp; Time</h3>
                <p className="text-lg text-gray-900">4th July 2026</p>
                <p className="text-gray-700">10:00 – 10:45 AM</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-1">Venue</h3>
                <p className="text-gray-900 leading-relaxed">{CORTEX_KEYNOTE_SPEECH_2026_VENUE}</p>
              </div>
            </div>
            {keynoteEvent?.posterImage && (
              <div className="flex justify-center md:justify-end md:sticky md:top-6">
                <img
                  src={encodeURI(keynoteEvent.posterImage)}
                  alt={keynoteEvent.posterAlt || 'CORTEX 2026 Keynote Speech Poster'}
                  className="w-full max-w-xs sm:max-w-sm object-contain rounded-lg shadow-md"
                />
              </div>
            )}
          </div>
        </AnimatedSection>

        <AnimatedSection
          className="mb-12"
          animateOnScroll={false}
          heading={<h2 className="text-2xl font-semibold text-gray-900 mb-6">Keynote Abstract</h2>}
        >
          <div className="bg-white rounded-lg shadow-md p-8 space-y-6">
            <h3 className="text-xl md:text-2xl font-semibold text-gray-900 text-center leading-snug">
              {CORTEX_KEYNOTE_SPEECH_2026_KEYNOTE_ABSTRACT_TITLE}
            </h3>
            <div className="space-y-4">
              {CORTEX_KEYNOTE_SPEECH_2026_KEYNOTE_ABSTRACT_PARAGRAPHS.map((paragraph, index) => (
                <p key={`keynote-abstract-${index}`} className="text-lg text-gray-700 leading-relaxed text-justify">
                  {linkifyTextToReact(paragraph)}
                </p>
              ))}
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection className="mb-12" animateOnScroll={false} heading={<h2 className="text-2xl font-semibold text-gray-900 mb-6">Biographical Statement</h2>}>
          <div className="bg-white rounded-lg shadow-md p-8 space-y-4">
            {CORTEX_KEYNOTE_SPEECH_2026_ABSTRACT.map((paragraph, index) => (
              <p key={`abstract-${index}`} className="text-lg text-gray-700 leading-relaxed text-justify">
                {linkifyTextToReact(paragraph)}
              </p>
            ))}
          </div>
        </AnimatedSection>

        <AnimatedSection
          className="mb-16"
          heading={<h2 className="text-2xl font-semibold text-gray-900 mb-6">Selected bibliography</h2>}
        >
          <div className="bg-white rounded-lg shadow-md p-8">
            <ol className="list-decimal list-outside space-y-3 pl-5 sm:pl-6 text-gray-700 leading-relaxed">
              {CORTEX_KEYNOTE_SPEECH_2026_BIBLIOGRAPHY.map((entry, index) => (
                <li key={`bib-${index}`} className="pl-2">
                  {entry}
                </li>
              ))}
            </ol>
          </div>
        </AnimatedSection>
      </div>
    </div>
  );
};

export default KeynoteSpeech2026;
